/**
 * useAIRecompile Hook
 *
 * Handles recompiling content layer changes into the flow's MMO layer.
 * Used by EditorAIPanel to apply AI-generated content updates.
 */

import { useContext, useCallback } from 'react';
import { MachineContext } from '../../fsm/useMachine';
import { FlowCompiler } from '../../../ai/compiler/FlowCompiler';
import { getThemeById, DEFAULT_THEME } from '../../../ai/styles/themeLibrary';
import { PresentationContent } from '../../../ai/schemas/ContentBlock';
import { FlowWindow, FlowContentLayer } from '../../utils/globals';
import Flow from '../../../model/Flow';

declare let window: FlowWindow;

/**
 * Get the content layer from the flow document
 */
function getContentLayer(): FlowContentLayer | null {
  if (window.flowboard?.content?.presentation) {
    return window.flowboard.content;
  }
  if (window.flow_json?.flowboard?.content?.presentation) {
    return window.flow_json.flowboard.content;
  }
  return null;
}

/**
 * Update the content layer in all possible locations
 */
function updateContentLayer(content: FlowContentLayer, contentMapping: any): void {
  // Update window.flowboard if it exists
  if (window.flowboard) {
    window.flowboard.content = content;
    window.flowboard.contentMapping = contentMapping;
  }
  // Update window.flow_json.flowboard if it exists
  if (window.flow_json?.flowboard) {
    window.flow_json.flowboard.content = content;
    window.flow_json.flowboard.contentMapping = contentMapping;
  }
}

export interface RecompileResult {
  success: boolean;
  error?: string;
}

/**
 * Hook for recompiling content layer changes into MMOs
 */
export function useAIRecompile() {
  const [current, send] = useContext(MachineContext);

  /**
   * Recompile the presentation content and update the flow
   */
  const recompile = useCallback(
    async (updatedPresentation: PresentationContent): Promise<RecompileResult> => {
      try {
        // Get current theme from content layer
        const contentLayer = getContentLayer();
        const themeId = contentLayer?.themeId || 'modern-light';
        const theme = getThemeById(themeId) || DEFAULT_THEME;

        // Get current view size from flow (stored at half resolution for retina)
        // Compiler needs full resolution, so multiply by 2
        const storedViewSize = current.context.flow.viewSize || { width: 512, height: 374 };
        const viewSize = {
          width: storedViewSize.width * 2,
          height: storedViewSize.height * 2,
        };

        // Create compiler with current theme
        const compiler = new FlowCompiler({
          theme,
          viewSize,
        });

        // Compile the updated presentation
        const result = compiler.compile(updatedPresentation);

        if (!result.flow || !result.flow.screens) {
          return {
            success: false,
            error: 'Compilation produced no valid screens',
          };
        }

        // Preserve flow metadata from current flow
        const currentFlow = current.context.flow;
        result.flow.id = currentFlow.id;
        result.flow.title = currentFlow.title;
        result.flow.created = currentFlow.created;
        result.flow.updated = new Date().toISOString();
        result.flow.themeId = currentFlow.themeId;

        // Update each screen while preserving screen IDs where possible
        const updatedScreens = result.flow.screens.map((newScreen, index) => {
          const existingScreen = currentFlow.screens[index];
          if (existingScreen) {
            // Preserve screen ID and other metadata
            newScreen.id = existingScreen.id;
            newScreen.created = existingScreen.created;
            newScreen.updated = new Date().toISOString();
          }
          return newScreen;
        });

        result.flow.screens = updatedScreens;

        // Update the content layer in all possible locations
        const updatedContent: FlowContentLayer = {
          presentation: updatedPresentation,
          themeId,
          layoutIds: result.layoutIds,
          lastCompiled: new Date().toISOString(),
          sessionId: contentLayer?.sessionId, // Preserve session ID
        };
        updateContentLayer(updatedContent, result.contentMapping);

        // Dispatch OVERRIDE_FLOW to update the entire flow
        send({ type: 'OVERRIDE_FLOW', flow: result.flow as Flow });

        // Re-initialize refs by setting index to current index
        // This triggers initializeRefs for the current screen's MMOs
        const currentIndex = current.context.index;
        setTimeout(() => {
          send({ type: 'SET_INDEX', index: currentIndex });
        }, 0);

        return { success: true };
      } catch (error) {
        console.error('AI Recompile error:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Recompilation failed',
        };
      }
    },
    [current.context.flow, current.context.index, send]
  );

  /**
   * Recompile a single screen only
   */
  const recompileScreen = useCallback(
    async (
      updatedPresentation: PresentationContent,
      screenIndex: number
    ): Promise<RecompileResult> => {
      try {
        // Get current theme from content layer
        const contentLayer = getContentLayer();
        const themeId = contentLayer?.themeId || 'modern-light';
        const theme = getThemeById(themeId) || DEFAULT_THEME;

        // Get current view size from flow (stored at half resolution for retina)
        // Compiler needs full resolution, so multiply by 2
        const storedViewSize = current.context.flow.viewSize || { width: 512, height: 374 };
        const viewSize = {
          width: storedViewSize.width * 2,
          height: storedViewSize.height * 2,
        };

        // Create compiler with current theme
        const compiler = new FlowCompiler({
          theme,
          viewSize,
        });

        // Compile just the updated screen content
        const result = compiler.compile(updatedPresentation);

        if (!result.flow.screens[screenIndex]) {
          return {
            success: false,
            error: `Screen ${screenIndex} not found in compiled result`,
          };
        }

        // Get the compiled screen
        const newScreen = result.flow.screens[screenIndex];
        const existingScreen = current.context.flow.screens[screenIndex];

        if (existingScreen) {
          // Preserve screen ID and metadata
          newScreen.id = existingScreen.id;
          newScreen.created = existingScreen.created;
          newScreen.updated = new Date().toISOString();
        }

        // Update the content layer in all possible locations
        const updatedContent: FlowContentLayer = {
          presentation: updatedPresentation,
          themeId,
          layoutIds: result.layoutIds,
          lastCompiled: new Date().toISOString(),
          sessionId: contentLayer?.sessionId, // Preserve session ID
        };
        updateContentLayer(updatedContent, result.contentMapping);

        // Dispatch OVERRIDE_SCREEN to update just this screen
        send({ type: 'OVERRIDE_SCREEN', screen: newScreen, screenIndex });

        // Only re-initialize refs if we're on the screen being recompiled
        // This prevents unnecessary screen change cascades during background image loading
        const currentIndex = current.context.index;
        if (currentIndex === screenIndex) {
          setTimeout(() => {
            send({ type: 'SET_INDEX', index: currentIndex });
          }, 0);
        }

        return { success: true };
      } catch (error) {
        console.error('AI Recompile screen error:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Screen recompilation failed',
        };
      }
    },
    [current.context.flow, current.context.index, send]
  );

  /**
   * Recompile the entire flow with a new theme
   */
  const recompileWithTheme = useCallback(
    async (newThemeId: string): Promise<RecompileResult> => {
      try {
        const contentLayer = getContentLayer();
        if (!contentLayer?.presentation) {
          return { success: false, error: 'No content layer available' };
        }

        const theme = getThemeById(newThemeId);
        if (!theme) {
          return { success: false, error: `Theme '${newThemeId}' not found` };
        }

        // Stored viewSize is half resolution for retina, compiler needs full resolution
        const storedViewSize = current.context.flow.viewSize || { width: 512, height: 374 };
        const viewSize = {
          width: storedViewSize.width * 2,
          height: storedViewSize.height * 2,
        };
        const compiler = new FlowCompiler({ theme, viewSize });
        const result = compiler.compile(contentLayer.presentation);

        if (!result.flow || !result.flow.screens) {
          return { success: false, error: 'Compilation produced no valid screens' };
        }

        // Preserve flow metadata
        const currentFlow = current.context.flow;
        result.flow.id = currentFlow.id;
        result.flow.title = currentFlow.title;
        result.flow.created = currentFlow.created;
        result.flow.updated = new Date().toISOString();
        result.flow.themeId = currentFlow.themeId;

        // Preserve screen IDs where possible
        const updatedScreens = result.flow.screens.map((newScreen, index) => {
          const existingScreen = currentFlow.screens[index];
          if (existingScreen) {
            newScreen.id = existingScreen.id;
            newScreen.created = existingScreen.created;
            newScreen.updated = new Date().toISOString();
          }
          return newScreen;
        });
        result.flow.screens = updatedScreens;

        // Update content layer with NEW themeId
        const updatedContent: FlowContentLayer = {
          presentation: contentLayer.presentation,
          themeId: newThemeId,
          layoutIds: result.layoutIds,
          lastCompiled: new Date().toISOString(),
          sessionId: contentLayer?.sessionId,
        };
        updateContentLayer(updatedContent, result.contentMapping);

        send({ type: 'OVERRIDE_FLOW', flow: result.flow as Flow });

        const currentIndex = current.context.index;
        setTimeout(() => {
          send({ type: 'SET_INDEX', index: currentIndex });
        }, 0);

        return { success: true };
      } catch (error) {
        console.error('AI Recompile with theme error:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Theme recompilation failed',
        };
      }
    },
    [current.context.flow, current.context.index, send]
  );

  return {
    recompile,
    recompileScreen,
    recompileWithTheme,
  };
}
